import { useState } from 'react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import { authApi, AuthApiError } from '../services/auth-api';

interface MfaSetupPageProps {
  onComplete?: () => void;
}

export function MfaSetupPage({ onComplete }: MfaSetupPageProps) {
  const [password, setPassword] = useState('');
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  const [verificationCode, setVerificationCode] = useState('');
  const [step, setStep] = useState<'password' | 'setup' | 'complete'>('password');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!password) {
      setError('Insira sua senha atual.');
      return;
    }

    setIsSubmitting(true);
    try {
      const accessToken = sessionStorage.getItem('gp_access_token') || '';
      const response = await authApi.enableMfa(accessToken, password);
      setQrCodeUrl(response.qrCodeUrl);
      setBackupCodes(response.backupCodes);
      setStep('setup');
    } catch (err) {
      if (err instanceof AuthApiError && err.statusCode === 403) {
        setError('Senha incorreta.');
      } else {
        setError('Erro ao configurar MFA. Tente novamente.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleVerifyCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!verificationCode || verificationCode.length !== 6) {
      setError('Insira um código de 6 digitos.');
      return;
    }

    // MFA is already enabled on the backend after enableMfa call
    // The verification step here is for the user to confirm they set up the app correctly
    setStep('complete');
    onComplete?.();
  };

  if (step === 'complete') {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', p: 2 }}>
        <Card sx={{ width: '100%', maxWidth: 480 }}>
          <CardContent sx={{ p: 4 }}>
            <Typography variant="h5" gutterBottom align="center" fontWeight={700}>
              MFA Ativado!
            </Typography>
            <Typography variant="body1" align="center" color="text.secondary" sx={{ mb: 2 }}>
              A autenticação em dois fatores foi configurada com sucesso.
            </Typography>
            <Alert severity="warning" sx={{ mb: 2 }}>
              Guarde seus códigos de backup em um local seguro. Eles sao a unica forma de acessar sua conta caso perca acesso ao autenticador.
            </Alert>
            <Box sx={{ bgcolor: 'grey.100', p: 2, borderRadius: 1, fontFamily: 'monospace', fontSize: 14 }}>
              {backupCodes.map((code, i) => (
                <Typography key={i} variant="body2" fontFamily="monospace">
                  {code}
                </Typography>
              ))}
            </Box>
          </CardContent>
        </Card>
      </Box>
    );
  }

  if (step === 'setup') {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', p: 2 }}>
        <Card sx={{ width: '100%', maxWidth: 480 }}>
          <CardContent sx={{ p: 4 }}>
            <Typography variant="h5" gutterBottom align="center" fontWeight={700}>
              Configurar MFA
            </Typography>
            <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 3 }}>
              Escaneie o QR code com seu aplicativo autenticador (Google Authenticator, Authy, etc.)
            </Typography>

            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
              <img src={qrCodeUrl} alt="QR Code MFA" width={200} height={200} />
            </Box>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            <Box component="form" onSubmit={handleVerifyCode} noValidate>
              <TextField
                label="Codigo de verificação"
                fullWidth
                margin="normal"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                inputProps={{ maxLength: 6, inputMode: 'numeric' }}
                autoFocus
              />
              <Button type="submit" variant="contained" fullWidth size="large" sx={{ mt: 2 }}>
                Verificar e Ativar
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', p: 2 }}>
      <Card sx={{ width: '100%', maxWidth: 420 }}>
        <CardContent sx={{ p: 4 }}>
          <Typography variant="h5" component="h1" gutterBottom align="center" fontWeight={700}>
            Ativar MFA
          </Typography>
          <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 3 }}>
            Confirme sua senha para configurar a autenticação em dois fatores.
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handlePasswordSubmit} noValidate>
            <TextField
              label="Senha atual"
              type="password"
              fullWidth
              margin="normal"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              autoFocus
              disabled={isSubmitting}
            />
            <Button
              type="submit"
              variant="contained"
              fullWidth
              size="large"
              disabled={isSubmitting}
              sx={{ mt: 2 }}
            >
              {isSubmitting ? <CircularProgress size={24} /> : 'Continuar'}
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
}
